import React, { useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  FaBalanceScale,
  FaCheckCircle,
  FaClipboardCheck,
  FaCopy,
  FaDownload,
  FaExclamationTriangle,
  FaFileAlt,
  FaPrint,
  FaRedo,
} from "react-icons/fa";

const COLORS = {
  bg: "#F6F4F0",
  ink: "#17161B",
  panel: "#FFFFFF",
  dark: "#111014",
  accent: "#E8481D",
  accentSoft: "#FCE7DF",
  green: "#16845B",
  greenSoft: "#E7F6EF",
  yellow: "#A76500",
  yellowSoft: "#FFF4D6",
  muted: "#77737F",
  border: "#E7E3DC",
};

const OFFICIAL_RULE_URL =
  "https://cppa.ca.gov/regulations/pdf/ccpa_updates_cyber_risk_admt_appr_text.pdf";

const EMPTY_FORM = {
  businessName: "",
  noticeDate: "",
  audience: "California consumers",
  interactionMethod: "Website and online account",
  decisionCategory: "Employment or independent contracting",
  specificDecision: "",
  specificPurpose: "",
  personalInfoCategories: "",
  processingExplanation: "",
  outputType: "",
  outputUse: "",
  humanRole: "",
  optOutStatus: "available",
  optOutUrl: "",
  optOutInstructions: "",
  appealInstructions: "",
  exceptionName: "",
  alternativeProcess: "",
  accessUrl: "",
  accessInstructions: "",
  privacyEmail: "",
  privacyPolicyUrl: "",
  accessibilityContact: "",
  additionalNotes: "",
};

const SAMPLE_FORM = {
  businessName: "Example Company, Inc.",
  noticeDate: "January 1, 2026",
  audience: "California job applicants",
  interactionMethod: "Online job application portal",
  decisionCategory: "Employment or independent contracting",
  specificDecision: "whether an applicant advances to a first-round interview",
  specificPurpose:
    "We use automated decisionmaking technology to screen submitted applications and identify candidates whose stated qualifications match the minimum requirements for the role.",
  personalInfoCategories:
    "Employment history, education, professional licenses, skills, application responses, and job-related assessment results.",
  processingExplanation:
    "The system compares job-related information in an application with the published requirements for the position and assigns a match score.",
  outputType:
    "A numerical match score and a recommendation to advance or not advance the application.",
  outputUse:
    "The recommendation is one factor used to determine whether the applicant advances to a first-round interview; it is not used for advertising.",
  humanRole:
    "A trained recruiter reviews the application, the system recommendation, and other job-related information and may change the recommendation before a decision is made.",
  optOutStatus: "available",
  optOutUrl: "https://example.com/privacy/admt-opt-out",
  optOutInstructions:
    "Submit the online form and select the hiring process for which you want to opt out.",
  appealInstructions: "",
  exceptionName: "",
  alternativeProcess:
    "If you opt out, a trained recruiter will review your application without using the automated match score or recommendation.",
  accessUrl: "https://example.com/privacy/admt-access",
  accessInstructions:
    "Submit the access request form and provide the information requested to verify your identity.",
  privacyEmail: "privacy@example.com",
  privacyPolicyUrl: "https://example.com/privacy",
  accessibilityContact:
    "Email privacy@example.com to request this notice in an alternative format.",
  additionalNotes: "",
};

const clean = (value) => String(value || "").trim();

const joinInstruction = (url, instructions) => {
  const parts = [];
  if (clean(instructions)) parts.push(clean(instructions));
  if (clean(url)) parts.push(`Online request: ${clean(url)}`);
  return parts.join(" ");
};

const buildNotice = (form) => {
  const optOutText =
    form.optOutStatus === "available"
      ? `You may ask us not to use this ADMT with respect to you. ${joinInstruction(
          form.optOutUrl,
          form.optOutInstructions
        )}`
      : form.optOutStatus === "human-appeal"
      ? `We rely on the human-appeal exception and do not provide an ADMT opt-out for this use. You may appeal the significant decision. ${clean(
          form.appealInstructions
        )}`
      : `We do not provide an ADMT opt-out for this use because we rely on the following exception: ${clean(
          form.exceptionName
        )}.`;

  const sections = [
    {
      title: "Pre-Use Notice About Automated Decisionmaking Technology",
      body: `${clean(form.businessName) || "[Business name]"} provides this notice before using automated decisionmaking technology (ADMT) with personal information relating to ${
        clean(form.audience) || "[consumer audience]"
      }.${clean(form.noticeDate) ? ` Notice date: ${clean(form.noticeDate)}.` : ""}`,
    },
    {
      title: "Why We Use ADMT",
      body: `${clean(form.specificPurpose) || "[Describe the specific purpose in plain language.]"}${
        clean(form.specificDecision)
          ? ` The significant decision concerns ${clean(form.specificDecision)}.`
          : ""
      }`,
    },
    {
      title: "Your Right to Opt Out or Appeal",
      body: optOutText,
    },
    {
      title: "Your Right to Access ADMT Information",
      body: `You may request information about our use of ADMT with respect to you. ${joinInstruction(
        form.accessUrl,
        form.accessInstructions
      )}`,
    },
    {
      title: "No Retaliation",
      body:
        "We will not retaliate against you for exercising your rights under the California Consumer Privacy Act.",
    },
    {
      title: "How the ADMT Works",
      body: [
        `Personal information used: ${
          clean(form.personalInfoCategories) ||
          "[List the categories of personal information that affect the output.]"
        }`,
        `Processing: ${
          clean(form.processingExplanation) ||
          "[Explain how the ADMT processes personal information.]"
        }`,
        `Output: ${clean(form.outputType) || "[Describe the output generated.]"}`,
        `Use in the decision: ${
          clean(form.outputUse) ||
          "[Explain how the output is used and whether it is the sole factor.]"
        }`,
        clean(form.humanRole)
          ? `Human role: ${clean(form.humanRole)}`
          : "Human role: [Explain any human role in the decisionmaking process.]",
      ].join("\n\n"),
    },
  ];

  if (form.optOutStatus === "available") {
    sections.push({
      title: "How the Decision Will Be Made If You Opt Out",
      body:
        clean(form.alternativeProcess) ||
        "[Describe the alternative process used for consumers who opt out.]",
    });
  }

  if (
    clean(form.privacyEmail) ||
    clean(form.privacyPolicyUrl) ||
    clean(form.accessibilityContact)
  ) {
    sections.push({
      title: "Contact and Additional Privacy Information",
      body: [
        clean(form.privacyEmail) ? `Privacy contact: ${clean(form.privacyEmail)}` : "",
        clean(form.privacyPolicyUrl)
          ? `Privacy policy: ${clean(form.privacyPolicyUrl)}`
          : "",
        clean(form.accessibilityContact)
          ? `Alternative format: ${clean(form.accessibilityContact)}`
          : "",
      ]
        .filter(Boolean)
        .join("\n"),
    });
  }

  if (clean(form.additionalNotes)) {
    sections.push({ title: "Additional Information", body: clean(form.additionalNotes) });
  }

  return sections;
};

const validateForm = (form) => {
  const checks = [
    ["Business name", clean(form.businessName)],
    ["Specific ADMT purpose", clean(form.specificPurpose)],
    ["Specific significant decision", clean(form.specificDecision)],
    ["Personal-information categories", clean(form.personalInfoCategories)],
    ["How ADMT processes information", clean(form.processingExplanation)],
    ["Type of output", clean(form.outputType)],
    ["How output affects the decision", clean(form.outputUse)],
    ["Human role or no-human statement", clean(form.humanRole)],
    [
      "ADMT access request method",
      clean(form.accessUrl) || clean(form.accessInstructions),
    ],
    ["Privacy contact", clean(form.privacyEmail)],
  ];

  if (form.optOutStatus === "available") {
    checks.push(
      ["ADMT opt-out method", clean(form.optOutUrl) || clean(form.optOutInstructions)],
      ["Alternative decision process", clean(form.alternativeProcess)]
    );
  } else if (form.optOutStatus === "human-appeal") {
    checks.push(["Appeal instructions", clean(form.appealInstructions)]);
  } else {
    checks.push(["Specific opt-out exception", clean(form.exceptionName)]);
  }

  const completed = checks.filter(([, result]) => Boolean(result)).length;
  return {
    checks: checks.map(([label, result]) => ({ label, complete: Boolean(result) })),
    completed,
    total: checks.length,
    score: Math.round((completed / checks.length) * 100),
    missing: checks.filter(([, result]) => !result).map(([label]) => label),
  };
};

const ADMTPreUseNoticeGenerator = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [activeStep, setActiveStep] = useState(1);
  const [showPreview, setShowPreview] = useState(true);

  const noticeSections = useMemo(() => buildNotice(form), [form]);
  const validation = useMemo(() => validateForm(form), [form]);

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const noticeText = useMemo(
    () =>
      noticeSections
        .map((section) => `${section.title}\n${section.body}`)
        .join("\n\n"),
    [noticeSections]
  );

  const useSample = () => {
    setForm(SAMPLE_FORM);
    setActiveStep(1);
    setShowPreview(true);
    console.log("[ADMT NOTICE GENERATOR] Sample data loaded.");
    toast.success("Sample notice loaded.");
  };

  const reset = () => {
    setForm(EMPTY_FORM);
    setActiveStep(1);
    console.log("[ADMT NOTICE GENERATOR] Form reset.");
    toast.success("Generator reset.");
  };

  const copyNotice = async () => {
    try {
      await navigator.clipboard.writeText(noticeText);
      console.log("[ADMT NOTICE GENERATOR] Notice copied.", {
        score: validation.score,
        missing: validation.missing,
      });
      toast.success("Notice copied.");
    } catch (error) {
      console.error("[ADMT NOTICE GENERATOR] Copy failed.", error);
      toast.error("Could not copy the notice.");
    }
  };

  const downloadText = () => {
    const blob = new Blob([noticeText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "admt-pre-use-notice.txt";
    anchor.click();
    URL.revokeObjectURL(url);
    console.log("[ADMT NOTICE GENERATOR] TXT downloaded.");
    toast.success("TXT downloaded.");
  };

  const downloadHtml = () => {
    const escapeHtml = (value) =>
      value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#039;");
    const body = noticeSections
      .map(
        (section) =>
          `<section><h2>${escapeHtml(section.title)}</h2><p>${escapeHtml(
            section.body
          ).replace(/\n/g, "<br>")}</p></section>`
      )
      .join("\n");
    const html = `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>ADMT Pre-Use Notice</title><style>body{font-family:Arial,sans-serif;max-width:820px;margin:40px auto;padding:0 20px;line-height:1.65;color:#17161b}h1,h2{line-height:1.25}h2{font-size:1.15rem;margin-top:1.8rem}a{color:#b52f10}@media(max-width:600px){body{margin:20px auto}}</style></head><body>${body}</body></html>`;
    const url = URL.createObjectURL(
      new Blob([html], { type: "text/html;charset=utf-8" })
    );
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "admt-pre-use-notice.html";
    anchor.click();
    URL.revokeObjectURL(url);
    console.log("[ADMT NOTICE GENERATOR] HTML downloaded.");
    toast.success("HTML downloaded.");
  };

  const textInput = (id, label, help, required = false) => (
    <div className="mb-3">
      <label htmlFor={id} className="form-label fw-bold">
        {label} {required && <span style={{ color: COLORS.accent }}>*</span>}
      </label>
      <input
        id={id}
        type={id.toLowerCase().includes("url") ? "url" : "text"}
        className="form-control"
        value={form[id]}
        onChange={(event) => setField(id, event.target.value)}
        style={{ borderColor: COLORS.border }}
      />
      {help && <div className="form-text">{help}</div>}
    </div>
  );

  const textArea = (id, label, help, required = false, rows = 4) => (
    <div className="mb-3">
      <label htmlFor={id} className="form-label fw-bold">
        {label} {required && <span style={{ color: COLORS.accent }}>*</span>}
      </label>
      <textarea
        id={id}
        className="form-control"
        rows={rows}
        value={form[id]}
        onChange={(event) => setField(id, event.target.value)}
        style={{ borderColor: COLORS.border, resize: "vertical" }}
      />
      {help && <div className="form-text">{help}</div>}
    </div>
  );

  return (
    <div style={{ background: COLORS.bg, color: COLORS.ink, minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={2600} />
      <div className="container py-5" style={{ maxWidth: 1200 }}>
    

        <div
          className="rounded-3 p-3 mb-4 small"
          style={{ background: COLORS.yellowSoft, color: "#694400" }}
        >
          <FaBalanceScale className="me-2" />
          <strong>Not legal advice.</strong> This drafting tool does not determine whether
          your business or technology is covered, whether an exception applies, or whether
          the final notice is legally sufficient. Have California privacy counsel review it
          before use.
        </div>

        <div className="row g-4">
          <div className="col-lg-7">
            <div className="card border-0 shadow-sm" style={{ borderRadius: 18 }}>
              <div className="card-body p-3 p-md-4">
                <div className="d-flex flex-wrap justify-content-between gap-2 mb-4">
                  <div className="d-flex flex-wrap gap-2">
                    {[1, 2, 3, 4].map((step) => (
                      <button
                        key={step}
                        type="button"
                        className="btn btn-sm"
                        onClick={() => setActiveStep(step)}
                        style={{
                          background: activeStep === step ? COLORS.dark : COLORS.bg,
                          color: activeStep === step ? "#fff" : COLORS.ink,
                          fontWeight: 700,
                        }}
                      >
                        {step}. {step === 1 ? "Business" : step === 2 ? "ADMT use" : step === 3 ? "Rights" : "Contact"}
                      </button>
                    ))}
                  </div>
                  <button type="button" className="btn btn-sm" onClick={useSample} style={{ color: COLORS.accent, fontWeight: 700 }}>
                    Load sample
                  </button>
                </div>

                {activeStep === 1 && (
                  <div>
                    <h2 className="h5 fw-bold mb-3">Business and notice context</h2>
                    <div className="row g-3">
                      <div className="col-md-7">
                        {textInput("businessName", "Business name", "Use the legal or consumer-facing business name.", true)}
                      </div>
                      <div className="col-md-5">
                        {textInput("noticeDate", "Notice date", "Example: January 1, 2026")}
                      </div>
                      <div className="col-md-6">
                        {textInput("audience", "Consumer audience", "Example: California job applicants")}
                      </div>
                      <div className="col-md-6">
                        {textInput("interactionMethod", "Primary interaction method", "Use this to plan how the notice will be presented.")}
                      </div>
                    </div>
                    <label htmlFor="decisionCategory" className="form-label fw-bold">Significant-decision category</label>
                    <select id="decisionCategory" className="form-select mb-3" value={form.decisionCategory} onChange={(event) => setField("decisionCategory", event.target.value)}>
                      <option>Financial or lending services</option>
                      <option>Housing</option>
                      <option>Education enrollment or opportunities</option>
                      <option>Employment or independent contracting</option>
                      <option>Healthcare services</option>
                    </select>
                    {textInput("specificDecision", "Specific decision", "Do not write only “to make a significant decision.” Describe the actual decision.", true)}
                  </div>
                )}

                {activeStep === 2 && (
                  <div>
                    <h2 className="h5 fw-bold mb-3">ADMT purpose and operation</h2>
                    {textArea("specificPurpose", "Specific purpose", "Explain why this ADMT is used without generic wording.", true)}
                    {textArea("personalInfoCategories", "Personal-information categories affecting the output", "List categories, not unnecessary individual data values.", true)}
                    {textArea("processingExplanation", "How the ADMT processes the information", "Give a plain-language explanation; do not expose protected trade secrets.", true)}
                    {textArea("outputType", "Type of output generated", "Examples: score, recommendation, prediction, ranking, or decision.", true, 3)}
                    {textArea("outputUse", "How the output is used", "Explain whether it is the sole factor and identify other factors.", true)}
                    {textArea("humanRole", "Human role", "Explain the reviewer's role and authority, or clearly say there is no human involvement.", true)}
                  </div>
                )}

                {activeStep === 3 && (
                  <div>
                    <h2 className="h5 fw-bold mb-3">Consumer rights and request methods</h2>
                    <label htmlFor="optOutStatus" className="form-label fw-bold">Opt-out position</label>
                    <select id="optOutStatus" className="form-select mb-3" value={form.optOutStatus} onChange={(event) => setField("optOutStatus", event.target.value)}>
                      <option value="available">Opt-out is available</option>
                      <option value="human-appeal">Human-appeal exception claimed</option>
                      <option value="other-exception">Another §7221(b) exception claimed</option>
                    </select>

                    {form.optOutStatus === "available" && (
                      <>
                        {textInput("optOutUrl", "ADMT opt-out URL", "The link title on your site should state what the consumer is opting out of.", true)}
                        {textArea("optOutInstructions", "Opt-out instructions", "Explain how to submit the request.", true, 3)}
                        {textArea("alternativeProcess", "Alternative decision process after opt-out", "Explain how the significant decision will be made without the opted-out ADMT.", true)}
                      </>
                    )}

                    {form.optOutStatus === "human-appeal" &&
                      textArea("appealInstructions", "Appeal instructions", "Explain how the consumer can appeal the significant decision.", true)}

                    {form.optOutStatus === "other-exception" &&
                      textArea("exceptionName", "Specific exception relied upon", "Identify the specific §7221(b) exception after legal review.", true)}

                    {textInput("accessUrl", "ADMT access-request URL", "Direct link to the access request form or portal.", true)}
                    {textArea("accessInstructions", "ADMT access-request instructions", "Explain how the consumer submits the request.", true, 3)}
                  </div>
                )}

                {activeStep === 4 && (
                  <div>
                    <h2 className="h5 fw-bold mb-3">Contact and publishing details</h2>
                    {textInput("privacyEmail", "Privacy contact email", "Provide a monitored contact method.", true)}
                    {textInput("privacyPolicyUrl", "Privacy policy URL", "Optional direct privacy-policy link.")}
                    {textArea("accessibilityContact", "Alternative-format instructions", "Explain how consumers with disabilities can request another format.", false, 3)}
                    {textArea("additionalNotes", "Additional information", "Optional business-specific information. Do not include trade secrets or details that compromise security, fraud prevention, or safety.")}
                    <div className="rounded-3 p-3 small" style={{ background: COLORS.greenSoft, color: COLORS.green }}>
                      Present the notice prominently at or before collecting the personal information that will be processed by ADMT, using the way your business primarily interacts with the consumer.
                    </div>
                  </div>
                )}

                <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                  <button type="button" className="btn btn-light" disabled={activeStep === 1} onClick={() => setActiveStep((step) => Math.max(1, step - 1))}>Previous</button>
                  <button type="button" className="btn text-white" disabled={activeStep === 4} onClick={() => setActiveStep((step) => Math.min(4, step + 1))} style={{ background: COLORS.accent }}>Next step</button>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-5">
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 18 }}>
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h2 className="h5 fw-bold mb-0"><FaClipboardCheck className="me-2" style={{ color: COLORS.accent }} />Draft readiness</h2>
                  <span className="fs-4 fw-bold">{validation.score}%</span>
                </div>
                <div className="progress mb-3" style={{ height: 9, background: COLORS.bg }}>
                  <div className="progress-bar" style={{ width: `${validation.score}%`, background: validation.score === 100 ? COLORS.green : COLORS.accent }} />
                </div>
                <div className="small mb-3" style={{ color: COLORS.muted }}>{validation.completed} of {validation.total} drafting fields completed</div>
                {validation.missing.slice(0, 5).map((item) => (
                  <div className="small mb-2" key={item} style={{ color: COLORS.accent }}>
                    <FaExclamationTriangle className="me-2" />Missing: {item}
                  </div>
                ))}
                {validation.missing.length > 5 && <div className="small" style={{ color: COLORS.muted }}>+ {validation.missing.length - 5} more missing fields</div>}
                {validation.score === 100 && <div className="rounded-3 p-3 small" style={{ background: COLORS.greenSoft, color: COLORS.green }}><FaCheckCircle className="me-2" />All drafting fields are complete. Legal review is still required.</div>}
              </div>
            </div>

            <div className="card border-0 text-white" style={{ borderRadius: 18, background: COLORS.dark }}>
              <div className="card-body p-4">
                <h2 className="h5 fw-bold mb-3"><FaFileAlt className="me-2" style={{ color: COLORS.accent }} />Included regulatory topics</h2>
                {["Specific ADMT purpose", "Opt-out, appeal, or exception", "Right to access ADMT", "No-retaliation statement", "Personal information and processing", "Output and decision use", "Human role", "Alternative process when applicable"].map((item) => (
                  <div className="d-flex gap-2 small mb-2" key={item}><FaCheckCircle className="mt-1 flex-shrink-0" style={{ color: COLORS.green }} /><span>{item}</span></div>
                ))}
                <a href={OFFICIAL_RULE_URL} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-light mt-3">Open official regulation</a>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm mt-4" style={{ borderRadius: 18 }}>
          <div className="card-body p-3 p-md-4">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
              <div>
                <h2 className="h4 fw-bold mb-1">Generated notice preview</h2>
                <div className="small" style={{ color: COLORS.muted }}>Review every statement for factual accuracy before publication.</div>
              </div>
              <button type="button" className="btn btn-sm btn-light" onClick={() => setShowPreview((value) => !value)}>{showPreview ? "Hide preview" : "Show preview"}</button>
            </div>

            {showPreview && (
              <div className="rounded-3 p-3 p-md-4 mb-4" style={{ background: "#FCFBF9", border: `1px solid ${COLORS.border}` }}>
                {noticeSections.map((section, index) => (
                  <section key={section.title} className={index ? "mt-4" : ""}>
                    {index === 0 ? <h3 className="h3 fw-bold">{section.title}</h3> : <h3 className="h5 fw-bold">{section.title}</h3>}
                    {section.body.split("\n").map((line, lineIndex) => line ? <p className="mb-2" key={`${section.title}-${lineIndex}`}>{line}</p> : <div className="mb-2" key={`${section.title}-${lineIndex}`} />)}
                  </section>
                ))}
              </div>
            )}

            <div className="d-flex flex-wrap gap-2">
              <button type="button" className="btn text-white" onClick={copyNotice} style={{ background: COLORS.accent, fontWeight: 700 }}><FaCopy className="me-2" />Copy notice</button>
              <button type="button" className="btn btn-outline-dark" onClick={downloadText}><FaDownload className="me-2" />Download TXT</button>
              <button type="button" className="btn btn-outline-dark" onClick={downloadHtml}><FaDownload className="me-2" />Download HTML</button>
              <button type="button" className="btn btn-light" onClick={() => window.print()}><FaPrint className="me-2" />Print</button>
              <button type="button" className="btn btn-light" onClick={reset}><FaRedo className="me-2" />Reset</button>
            </div>
          </div>
        </div>

        <div className="small mt-4" style={{ color: COLORS.muted }}>
          Regulatory basis: California Code of Regulations, title 11, §§ 7003 and 7220–7222. Regulations effective January 1, 2026. Last content review: August 30, 2026.
        </div>
      </div>
    </div>
  );
};

export default ADMTPreUseNoticeGenerator;

